import i18n from "../i18n";
import React, { useState, useEffect, useRef  } from 'react';
import axios from 'axios';
import { Rnd } from "react-rnd";
import { useDrop } from "react-dnd";
import { DndProvider, useDrag } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { useLayoutImage } from "../context/LayoutContext";
import './Layout.css'; // Add custom styles
import config from '../config';
import { renderSeatDots } from './shared/renderSeatDots';

const tr = (...args) => i18n.t(...args);

const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
};

const Table = ({ table, marginX, marginY, moveTable, className = '' }) => {
  const [cursorOffset, setCursorOffset] = useState({ x: 0, y: 0 });

  const handlePointerDown = (event) => {
    const tableElement = event.currentTarget.getBoundingClientRect();

    const point = event.touches && event.touches[0]
      ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
      : { x: event.clientX, y: event.clientY };

    // Compute cursor offset inside the table
    const relativeX = point.x - tableElement.left;
    const relativeY = point.y - tableElement.top;

    setCursorOffset({ x: Math.round(relativeX), y: Math.round(relativeY) });

    //console.log(`Cursor relative to table: (${Math.round(relativeX)}, ${Math.round(relativeY)})`);
  };

  const [{ isDragging }, drag] = useDrag({
    type: "TABLE",
    item: { id: table.id, number: (table.name ?? table.number), width: table.width, height: table.height, offsetX: cursorOffset.x, offsetY: cursorOffset.y },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const displayNumber = table.name ?? table.number;

  return (
    <div
      ref={drag}
      onMouseDown={handlePointerDown}
      onTouchStart={handlePointerDown}
      className={`table ${className}`}
      style={{
        opacity: isDragging ? 0.5 : 1,
        width: table.width + "px",
        height: table.height + "px",
        left: table.area ? (Math.round((Number(table.area_x || 0)) / 10) * 10 + "px") : undefined,
        top: table.area ? (Math.round((Number(table.area_y || 0)) / 10) * 10 + "px") : undefined,
        background: "#4CAF50",
        cursor: "grab",
        position: table.area ? "absolute" : "relative",
      }}
    >
      {displayNumber}
      
      <div className="table-seats-wrapper">
        {renderSeatDots(table, marginX, marginY)}
      </div>
    </div>
  );
};

const Layout = () => {

  const [layoutSrc, setLayoutSrc] = useState(null);
  const layoutFromCtx = useLayoutImage();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [deletedTables, setDeletedTables] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [tabLabels, setTabLabels] = useState([]);
  const [newArea, setNewArea] = useState({
    name: 'New Area',
    smoking: false,
    fireplace: false,
    vip: false,
    inside: false,
    outside: false,
    view: false,
    garden: false,
    comment: ''
  });
  const [changesDone, setChangesDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [flyAnim, setFlyAnim] = useState(null);
  const flyAnimTimerRef = useRef(null);

  // Touch/tablet: react-rnd often starts a drag on touch; we'll treat tiny drags as taps.
  const lastTouchTsRef = useRef(0);
  const dragStartRef = useRef({});
  const TAP_SLOP = 8; // px

  useEffect(() => {
    setLayoutSrc(layoutFromCtx);
  }, [layoutFromCtx]);

  const handleFileChange = (event) => {
      setFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("layout", file);

    try {
        await axios.post(tr('{{API_BASE_URL}}/_companies/layout', {
          API_BASE_URL: config.API_BASE_URL
        }), formData, {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
        });

        // Reload the image after upload
        const newImageUrl = URL.createObjectURL(file);
        setLayoutSrc(newImageUrl);
    } catch (error) {
        console.error("Upload failed:", error);
    } finally {
        setUploading(false);
    }
  };

  const fetchTables = async () => {
    try {
      axios.get(tr('{{API_BASE_URL}}/tables', {
        API_BASE_URL: config.API_BASE_URL
      }))
        .then((response) => {
          let data = response.data;
          data.forEach(datum => {
            datum.x = datum.monitor_x;
            datum.y = datum.monitor_y;
            datum.name = datum.number;
          });
          setTables(data);
        })
        .catch((error) => console.error('Error fetching tables:', error));
    } catch (error) {
      console.error("Error fetching tables:", error);
    }
  };

  const fetchAreas = () => {
    axios.get(tr('{{API_BASE_URL}}/areas', {
      API_BASE_URL: config.API_BASE_URL
    }))
    .then((response) => {
      response.data.push(newArea);
      setAreas(response.data) ;
    })
    .catch((error) => console.error('Error fetching areas:', error));
  }

  useEffect(() => {
    fetchAreas();
    fetchTables();    
  }, []);

  useEffect(() => {
    setTabLabels(['Layout', ...areas.map(area => area.name)]);
  }, [areas]);

  const addTable = () => {
    setTables(prevTables => {
      // Take only numeric names for numbering
      const numericNames = prevTables
        .map(t => Number(t.name))
        .filter(n => Number.isFinite(n) && n > 0);
  
      const nextNumber = numericNames.length
        ? Math.max(...numericNames) + 1
        : 1;
  
      const newTable = {
        insert: true,
        id: Date.now(),          // temporary client-side id
        name: nextNumber,        // display number, still numeric
        capacity: 4,
        x: 10 + (30 * nextNumber) % 150,
        y: 5 + (10 * nextNumber) % 100,
        width: 30,
        height: 30,
        area: null,              // optional: keeps it consistent
      };
  
      // add & select the new table in a single state update
      return [...prevTables, { ...newTable, selected: true }];
    });
  
    setChangesDone(true);
  };

  const updateTable = (id, updates) => {
    setTables((prevTables) =>
      prevTables.map((table) =>
        table.id === id ? { ...table, ...updates } : table
      )
    );
    setChangesDone(true);
  };

  const saveTables = async () => {
    setSaving(true);
    try {
      // Prepare deletes (for existing tables)
      const deletePromises = deletedTables.map((id) =>
        axios.delete(
          tr('{{API_BASE_URL}}/tables/{{id}}', {
            API_BASE_URL: config.API_BASE_URL,
            id,
          })
        )
      );

      // Update existing tables
      const updatePromises = tables
        .filter((t) => !t.insert)
        .map((table) => {
          const payload = { ...table };
          payload.monitor_x = payload.x;
          payload.monitor_y = payload.y;
          payload.number = payload.name;

          // avoid sending UI-only fields
          delete payload.selected;
          delete payload.wasDragged;
          delete payload.name;

          return axios.put(
            tr('{{API_BASE_URL}}/tables/{{id}}', {
              API_BASE_URL: config.API_BASE_URL,
              id: table.id,
            }),
            payload
          );
        });

      // Insert new tables, then persist area assignment (0,0 etc) via /tables/area/<id>
      const insertPromises = tables
        .filter((t) => !!t.insert)
        .map(async (table) => {
          const payload = { ...table };
          payload.monitor_x = payload.x;
          payload.monitor_y = payload.y;
          payload.number = payload.name;

          // don't send client-only fields / temp id
          delete payload.id;
          delete payload.insert;
          delete payload.selected;
          delete payload.wasDragged;
          delete payload.name;

          const res = await axios.post(
            tr('{{API_BASE_URL}}/tables', {
              API_BASE_URL: config.API_BASE_URL,
            }),
            payload
          );

          const newId =
            res?.data?.id ??
            res?.data?.table?.id ??
            res?.data?.data?.id ??
            res?.data?.insertId;

          // If user already assigned an area from "Selected tables", persist it after insert.
          if (newId && table.area != null) {
            await axios.put(
              tr('{{API_BASE_URL}}/tables/area/{{tableId}}', {
                API_BASE_URL: config.API_BASE_URL,
                tableId: newId,
              }),
              {
                area: table.area,
                area_x: table.area_x ?? 0,
                area_y: table.area_y ?? 0,
              }
            );
          }
        });

      await Promise.all([...updatePromises, ...insertPromises, ...deletePromises]);

      setDeletedTables([]);
      setChangesDone(false);
      fetchTables();
    } catch (error) {
      console.error('Error saving or deleting tables:', error);
    } finally {
      setSaving(false);
    }
  };

  const addArea = () => {
    axios.post(tr('{{API_BASE_URL}}/areas', {
      API_BASE_URL: config.API_BASE_URL
    }), areas[areas.length-1])
      .then(response => {
        fetchAreas();
      })
      .catch(error => console.error('Error adding area:', error));
  };

  const deleteArea = (id) => {
    if(id){
      if(window.confirm("Do you want to delete this area?")){
          axios.delete(tr('{{API_BASE_URL}}/areas/{{id}}', {
            API_BASE_URL: config.API_BASE_URL,
            id
          }))
          .then(() => {
            fetchAreas();
          })
          .catch(error => console.error('Error deleting area:', error));
        }
      }else{
         let newArea = {
           name: 'New Area',
           smoking: false, fireplace: false, vip: false,
           inside: false, outside: false, view: false, garden: false,
           comment: ''
        };
        setNewArea(newArea);
        areas[areas.length-1] = newArea;
        setAreas(areas);
        setTabLabels(['Layout', ...areas.map(area => area.name)]);
      }
    setActiveTab(0);
  };

  const updateAreaField = (id, field, value) => {
    const updatedAreas = areas.map(area =>
      area.id === id ? { ...area, [field]: value } : area
    );
    setAreas(updatedAreas);
  };

  const saveAreaChanges = (id) => {
    const area = areas.find(a => a.id === id);
    axios.put(tr('{{API_BASE_URL}}/areas/{{id}}', {
      API_BASE_URL: config.API_BASE_URL,
      id
    }), area)
      .then(() => setActiveTab(0))
      .catch(error => console.error('Error updating area:', error));
  };

  const updateTableArea = async (tableId, areaId, areaX, areaY) => {
    // Ensure snapping to 10px grid (allow 0,0)
    const snappedX = areaId ? Math.max(0, Math.round((areaX || 0) / 10) * 10) : null;
    const snappedY = areaId ? Math.max(0, Math.round((areaY || 0) / 10) * 10) : null;

    // Optimistic local update (keeps selections & unsaved inserts intact)
    setTables((prev) =>
      prev.map((t) =>
        t.id === tableId
          ? {
              ...t,
              area: areaId || null,
              area_x: areaId ? snappedX : null,
              area_y: areaId ? snappedY : null,
            }
          : t
      )
    );

    // For unsaved (insert) tables we can't call the area endpoint yet.
    const local = tables.find((t) => t.id === tableId);
    if (local?.insert) {
      setChangesDone(true);
      return;
    }

    try {
      await axios.put(
        tr('{{API_BASE_URL}}/tables/area/{{tableId}}', {
          API_BASE_URL: config.API_BASE_URL,
          tableId,
        }),
        {
          area: areaId || null,
          area_x: areaId ? snappedX : null,
          area_y: areaId ? snappedY : null,
        }
      );
    } catch (error) {
      console.error("Error updating table area:", error);
      // Re-sync from server if something went wrong
      fetchTables();
    }
  };

  const toggleTableSelection = (tableId) => {
    setTables((prevTables) =>
      prevTables.map((table) =>
        table.id === tableId && activeTab === 0 // Only allow selection in Layout tab
          ? { ...table, selected: !table.selected }
          : table
      )
    );
  };

  const unselectTable = (tableId) => {
    setTables((prev) =>
      prev.map((t) => (t.id === tableId ? { ...t, selected: false } : t))
    );
  };

  const triggerFlyAnim = (tableId) => {
    setFlyAnim({ id: tableId, key: Date.now() });
    if (flyAnimTimerRef.current) clearTimeout(flyAnimTimerRef.current);
    flyAnimTimerRef.current = setTimeout(() => setFlyAnim(null), 650);
  };



  const handleSelectedTableAreaChange = (table, value) => {
    const areaId = value === "" ? null : Number(value);
    const tabIndex = areaId ? (areas.findIndex((a) => Number(a.id) === Number(areaId)) + 1) : 0;

    // Unassign
    if (!areaId) {
      if (table.insert) {
        updateTable(table.id, { area: null, area_x: null, area_y: null });
      } else {
        updateTableArea(table.id, null, null, null);
      }
      setActiveTab(0);
      return;
    }

    const sameArea = Number(table.area) === areaId;
    const hasRealPos =
      table.area_x !== null &&
      table.area_x !== undefined &&
      table.area_y !== null &&
      table.area_y !== undefined &&
      (Number(table.area_x) !== 0 || Number(table.area_y) !== 0);

    // If already in this area with a real position, keep it there.
    if (sameArea && hasRealPos) return;

    if (table.insert) {
      // Keep it local until saved
      updateTable(table.id, { area: areaId, area_x: 0, area_y: 0 });
      triggerFlyAnim(table.id);
      if (tabIndex > 0) setActiveTab(tabIndex);
    } else {
      // Persist immediately
      updateTableArea(table.id, areaId, 0, 0);
      triggerFlyAnim(table.id);
      if (tabIndex > 0) setActiveTab(tabIndex);
    }
  };


const isResizeHandle = (target) => target?.closest?.(".react-rnd-handle");

const isTouchEvent = (evt) => {
  return !!(evt?.touches || evt?.changedTouches) || evt?.pointerType === "touch";
};

const deleteSelectedTables = async () => {
    setDeleting(true);
    try {
      // Remove unsaved selected tables locally
      setTables((prev) => prev.filter((t) => !(t.selected && t.insert)));

    
      const existingTableIds = tables
        .filter((table) => table.selected && !table.insert)
        .map((table) => table.id);
  
      // Delete from database
      await Promise.all(
        existingTableIds.map(id => {
          return axios.delete(tr('{{API_BASE_URL}}/tables/{{id}}', {
            API_BASE_URL: config.API_BASE_URL,
            id
          }));
        }
        )
      );
      setChangesDone(false);
      fetchTables();
    } catch (error) {
      console.error("Error deleting tables:", error);
    } finally {
      setDeleting(false);
    }
  };

  const Tank = ({ tables, moveTable }) => {

    const [{ isOver }, drop] = useDrop({
      accept: "TABLE",
      drop: (item) => {
        console.log(tr('Table {{id}} dropped in the tank', {
          id: item.id
        }));
        moveTable(item.id, null, null, null);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    return (
      <div
        ref={drop}
        className="tank"
        style={{ backgroundColor: isOver ? "rgba(255, 0, 0, 0.2)" : "transparent" }}
      >
        <h3>{tr('Drag & Drop Unassigned Tables')}</h3>
        {tables.map((table) => (
          <Table key={table.id} table={table} marginX={2} marginY={2} moveTable={moveTable} />
        ))}
      </div>
    );
  };

  const AreaDropZone = ({ area, updateTableArea }) => {
    const dropRef = useRef(null);

    const [{ isOver }, drop] = useDrop({
      accept: "TABLE",
      drop: (item, monitor) => {
        if (!dropRef.current) return;
  
        const dropPosition = monitor.getClientOffset();
        const areaElement = dropRef.current.getBoundingClientRect();
  
        if (!dropPosition || !areaElement) return;
        
        const x = dropPosition.x - areaElement.left - item.offsetX - 5;
        const y = dropPosition.y - areaElement.top - item.offsetY - 5;
  
        // Snap to 10px grid
        const snappedX = Math.round(x / 10) * 10;
        const snappedY = Math.round(y / 10) * 10;
  
        //console.log(`Dropped at: (${snappedX}, ${snappedY}) inside Area: ${area.name}`);
  
        updateTableArea(item.id, area.id, snappedX, snappedY);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    return (
      <div
        ref={(node) => {
          drop(node);
          dropRef.current = node;
        }}
        className="area"
        style={{
          backgroundColor: isOver ? "rgba(0, 255, 0, 0.2)" : "transparent",
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        <div className="grid-overlay"></div>
        <i>{tr('Drag & Drop Tables Here')}</i>
      </div>
    );
  };

  const renderTabContent = () => {
    if (activeTab === 0) {
      return (
        <div className="restaurant-editor">
          <div className="layout">
            <div style={{ backgroundImage: tr('url({{layoutSrc}})', {
              layoutSrc
            }), maxWidth: '800px', width: '800px', height: '800px', backgroundSize: 'contain', backgroundRepeat: 'no-repeat' }}/>
            {tables.map((table) => (
                <Rnd
                  key={table.id}
                  className={`edit-table ${table.selected ? 'selected' : ''}`}
                  bounds="parent"
                  cancel=".table-capacity"
                  size={{ width: table.width, height: table.height }}
                  position={{
                    x: table.x,
                    y: table.y
                  }}
                  grid={[10, 10]} // Enables snapping to grid  
                  onDragStart={(e, d) => {
                    table.wasDragged = table.x + ":" + table.y + ":" + table.width + ":" + table.height;
                    const touch = isTouchEvent(e);
                    if (touch) lastTouchTsRef.current = Date.now();
                    dragStartRef.current[table.id] = { x: table.x, y: table.y, isTouch: touch };
                  }}
                  onDragStop={(e, d) => { 
                    const snappedX = Math.round(d.x / 10) * 10;
                    const snappedY = Math.round(d.y / 10) * 10;

                    // Touch/tablet: if react-rnd started a drag but we barely moved, treat it as a tap selection.
                    const start = dragStartRef.current[table.id];
                    delete dragStartRef.current[table.id];
                    if (start?.isTouch) {
                      const dx = Math.abs(snappedX - (start.x ?? 0));
                      const dy = Math.abs(snappedY - (start.y ?? 0));
                      if (dx <= TAP_SLOP && dy <= TAP_SLOP) {
                        // Don't move the table or enable Save; just toggle selection.
                        delete table.wasDragged;
                        toggleTableSelection(table.id);
                        return;
                      }
                    }

                    if (activeTab === 0) {
                      // In Layout tab, update monitor_x and monitor_y
                      updateTable(table.id, { x: snappedX, y: snappedY });                        
                    } else {
                      // In an area tab, update area_x and area_y
                      updateTableArea(table.id, areas[activeTab - 1]?.id, snappedX, snappedY);
                    } 
                    if (table.wasDragged !== (snappedX+':'+snappedY+':'+table.width+':'+table.height)){
                      delete table.wasDragged;
                      setChangesDone(true);
                    } 
                  }}
                  onResizeStop={(e, direction, ref, delta, position) => {
                    if(!activeTab){
                      updateTable(table.id, {
                        width: Math.max(30, Math.round(ref.offsetWidth / 10) * 10),
                        height: Math.max(30, Math.round(ref.offsetHeight / 10) * 10),
                        ...position,
                      }) 
                      setChangesDone(true);
                    }
                  }}
                  onClick={(e) => {
                    if (activeTab !== 0) return;
                    // On touch devices a synthetic click may fire after touchend; ignore it.
                    if (Date.now() - lastTouchTsRef.current < 800) return;
                    if (isResizeHandle(e.target)) return;
                    toggleTableSelection(table.id);
                  }}
                >

                <div>
                  <input type="text" title={"Table #"+table?.name} value={table.name} className="table-number" onChange={(e) => updateTable(table.id, { name: e.target.value })}/>
                  {table.selected && (
                    <input type="number" title={"Capacity #"+table?.capacity} min="2" value={table.capacity} className="table-capacity" onChange={(e) => updateTable(table.id, { capacity: e.target.value })} onClick={(e) => e.stopPropagation()}/>
                  )}
                </div>
                            
                <div className="table-seats-wrapper">
                  {renderSeatDots(table, -1, -1)}
                </div>
       
                
              </Rnd>
            ))}
          </div>
        </div>
      );
    } else {
      const area = areas[activeTab - 1];
      return (
        <div className="area-tab">
          <div className="area-fields">
            <label>
              <input
                type="text"
                value={area.name}
                placeholder={tr('New Area')}
                style={{width: '165px'}}
                onChange={(e) => updateAreaField(area.id, 'name', e.target.value)}
              />
            </label>
            <label>
              <input type="checkbox"
                checked={area.garden}
                onChange={e => updateAreaField(area.id, 'garden', e.target.checked)}
              />{tr('Garden')}</label>            
            <label>
              <input
                type="checkbox"
                checked={area.smoking}
                onChange={(e) => updateAreaField(area.id, 'smoking', e.target.checked)}
              />{tr('Smoking')}</label>
            <label>
              <input type="checkbox"
                checked={area.outside}
                onChange={e => updateAreaField(area.id, 'outside', e.target.checked)}
              />{tr('Outside')}</label>
            <label>
              <input type="checkbox"
                checked={!!area.inside}
                onChange={e => updateAreaField(area.id, 'inside', e.target.checked)}
              />{tr('Inside')}</label>
            <label>
              <input type="checkbox"
                checked={area.view}
                onChange={e => updateAreaField(area.id, 'view', e.target.checked)}
              />{tr('View')}</label>
            <label>
              <input
                type="checkbox"
                checked={area.fireplace}
                onChange={(e) => updateAreaField(area.id, 'fireplace', e.target.checked)}
              />{tr('Fireplace')}</label>
            <label>
              <input
                type="checkbox"
                checked={area.vip}
                onChange={(e) => updateAreaField(area.id, 'vip', e.target.checked)}
              />{tr('VIP')}</label>
            <label>
              <textarea
                value={area.comment || ''}
                onChange={(e) => updateAreaField(area.id, 'comment', e.target.value)}
                placeholder={tr('Comment')}
              />
            </label>
            <div style={{display: 'inine-flex'}}>
              {area.id ? (
                <button style={{backgroundColor: 'darkOrange'}} onClick={() => saveAreaChanges(area.id)}>{tr('Update')}</button>
              ) : (
                <button style={{backgroundColor: 'darkGreen'}} onClick={() => addArea()}>{tr('Insert')}</button>
              )}
              <button style={{backgroundColor: 'darkRed', float: 'right'}} onClick={() => deleteArea(area.id)}>{tr('Delete')}</button>
            </div>
          </div>
          <div className="area-layout">
            {area && (
              <AreaDropZone area={area} updateTableArea={updateTableArea} />
            )}
            {tables
              .filter((table) => table.area === areas[activeTab - 1]?.id)
              .map((table) => {
                const isFly = flyAnim && flyAnim.id === table.id;
                const flyKey = isFly ? `${table.id}-${flyAnim.key}` : `${table.id}`;
                return (
                  <Table
                    key={flyKey}
                    table={table}
                    className={isFly ? "table-flyin" : ""}
                    marginX={2}
                    marginY={2}
                    moveTable={updateTableArea}
                  />
                );
              })}
          </div>
        </div>
      );
    }
  };

  const dndIsTouch = isTouchDevice();

  return (
    <DndProvider
      backend={dndIsTouch ? TouchBackend : HTML5Backend}
      options={dndIsTouch ? { enableMouseEvents: true, delayTouchStart: 60, touchSlop: 12 } : undefined}
    >
      <div className="layout-topbar">
      <h2 className="layout-title">{tr("Layout's Editor")}</h2>

        <div className="toolbar toolbar-top">
          <button disabled={saving || deleting} onClick={addTable}>{tr('Add Table')}</button>
                      <button disabled={!changesDone || saving || deleting} onClick={saveTables}>{saving ? tr('Saving...') : tr('Save Changes')}</button>
                      
                      {tables.filter((t)=>t.selected).length > 0 && activeTab === 0 && (
                        <button disabled={saving || deleting} onClick={deleteSelectedTables} style={{ backgroundColor: "darkRed", color: 'white', borderRadius: '3px', opacity: (saving || deleting) ? 0.6 : 1 }}>{deleting ? tr('Deleting...') : tr('Delete Selected')}</button>
                      )}    
                      
                      <div style={{marginLeft: '30px'}}>
                        <b>{tr('Update Layout:')}</b> <input type="file" accept="image/jpg" onChange={handleFileChange} />
                        <button onClick={handleUpload} disabled={!file || uploading || saving || deleting}>
                            {uploading ? tr("Uploading...") : tr("Upload")}
                        </button>
        </div>
          </div>

    </div>
      <div className="layout-container">
        <div className="layout-tabs">
          <div className="tabs-header">
            {tabLabels.map((label, index) => (
              <button className={activeTab === index ? 'active' : ''} key={index} onClick={() => setActiveTab(index)}>{tr(label)}</button>
            ))}
          </div>
          <div className="layout-content">
            {renderTabContent()}
          </div>
        </div>

        <div className="layout-sidebar">
          {tables.filter((t) => t.selected).length > 0 && (
            <div className="selected-tables-editor">
              <div className="selected-tables-editor-title">
                <b>{tr('Selected tables')}</b>
              </div>

              <table className="selected-tables-table">
                <thead>
                  <tr>
                    <th className="st-col-unselect" title={tr('Unselect')}></th>
                    <th className="st-col-number">{tr('Table')}</th>
                    <th className="st-col-capacity">{tr('Capacity')}</th>
                    <th className="st-col-area">{tr('Area')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tables
                    .filter((t) => t.selected)
                    .map((t) => (
                      <tr key={t.id}>
                        <td className="st-col-unselect">
                          <button
                            type="button"
                            className="st-unselect-btn"
                            title={tr('Unselect')}
                            onClick={() => unselectTable(t.id)}
                            disabled={saving || deleting}
                          >
                            ✕
                          </button>
                        </td>
                        <td className="st-col-number">
                          <input
                            type="text"
                            className="st-input st-number"
                            value={t.name}
                            onChange={(e) => updateTable(t.id, { name: e.target.value })}
                          />
                        </td>
                        <td className="st-col-capacity">
                          <input
                            type="number"
                            className="st-input st-capacity"
                            min="1"
                            value={t.capacity ?? ""}
                            onChange={(e) => updateTable(t.id, { capacity: e.target.value })}
                          />
                        </td>
                        <td className="st-col-area">
                          <select
                            className="st-select st-area"
                            value={t.area ?? ""}
                            onChange={(e) => handleSelectedTableAreaChange(t, e.target.value)}
                          >
                            <option value="">{tr('Unassigned')}</option>
                            {areas
                              .filter((a) => a.id)
                              .map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.name}
                                </option>
                              ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          <Tank tables={tables.filter((table) => table.area === null)} moveTable={updateTableArea} />
        </div>

      </div>
    </DndProvider>
  );
};

export default Layout;
